/** One full readiness pipeline. Hydration supplies an accepted, fresh snapshot. */
import {
  appendAuditFindings,
  auditPageCoverage,
  FULL_AUDIT_CHECK_IDS,
  runAuditCooperatively
} from './audit-engine.js';
import {
  analyzeAdoptable,
  makeTemplateOwnedAssetsFinding,
  makeReachabilityFindings,
  probeAssetReachability
} from '../core/asset-adoption.js';
import { assembleLiveTemplateCSS } from './template-support-css.js';

export async function runFullAudit(
  snapshot,
  {
    signal = null,
    scope = null,
    shouldContinue = () => true,
    hydrate = async (state) => ({
      snapshot: state,
      coverage: auditPageCoverage(state),
      revision: null
    }),
    deps = {}
  } = {}
) {
  const current = () => !signal?.aborted && shouldContinue();
  const loadTemplateCSS = deps.loadTemplateCSS || assembleLiveTemplateCSS;
  const probeReachability = deps.probeReachability || probeAssetReachability;
  const now = deps.now || Date.now;
  if (!current()) return null;
  try {
    const accepted = await hydrate(snapshot, {
      signal,
      shouldContinue: current
    });
    if (!accepted?.snapshot || !current()) return null;
    const state = accepted.snapshot;
    const coverage = accepted.coverage || auditPageCoverage(state);
    let report = await runAuditCooperatively(state, {
      tier: 'full',
      signal,
      scope,
      shouldContinue: current,
      revision: accepted.revision
    });
    if (!report || !current()) return null;
    const templateCSS = await loadTemplateCSS(state);
    if (!current()) return null;
    const analyzed = analyzeAdoptable(state, { templateCSS });
    const templateFinding = makeTemplateOwnedAssetsFinding(
      analyzed.templateOwned,
      state.sourceTemplateDomain || ''
    );
    const probe = await probeReachability(state, { signal });
    if (!current()) return null;
    report = appendAuditFindings(report, [
      templateFinding,
      ...makeReachabilityFindings(probe.failures)
    ]);
    const pending = new Set(coverage.hydrationFailed?.length ? FULL_AUDIT_CHECK_IDS : []);
    const assets = {
      total: analyzed.stateOwned.length,
      checked: probe.checked || 0,
      skipped: probe.skipped || null
    };
    if (assets.skipped || assets.checked < assets.total) pending.add('asset-reachability');
    const checkedAt = now();
    return {
      ...report,
      projectId: state.projectId || null,
      revision: accepted.revision ?? null,
      tiers: ['cheap', 'full'],
      coverage: { ...coverage, assets },
      pending: [...pending],
      checkedAt,
      checkedAtByTier: { cheap: checkedAt, full: checkedAt }
    };
  } catch (error) {
    if (error?.name === 'AbortError' || !current()) return null;
    throw error;
  }
}
